import { sendEVMTransaction, EVMTransactionError } from './evm-transaction';
import { wagmiAdapter } from '../config/appkit';
import { switchChain, getChainId } from '@wagmi/core';
import { 
  SupportedNetwork, 
  NETWORK_CONFIGS, 
  isValidNetwork
} from '../config/tokens';

interface TokenArgs {
  address: string;
  decimals?: number;
  spender?: string;
  intent?: 'transfer' | 'transferFrom';
}

interface PaymentArgs {
  network: SupportedNetwork;
  to: string;
  amount: string; // Human readable amount
  token?: TokenArgs;
}

interface PaymentCallbacks {
  onSwitching?: () => void;
  onAwaitingApproval?: () => void;
  onAwaitingSignature?: () => void;
  onBroadcasting?: () => void;
}

interface PaymentResult {
  hash: string;
  success: true;
}

export class PaymentError extends Error {
  constructor(
    message: string,
    public code?: string,
    public userMessage?: string
  ) {
    super(message);
    this.name = 'PaymentError';
  }
}


/**
 * Universal EVM payment sender
 * Works with all wallets (MetaMask, Coinbase, Trust, etc.)
 */
export async function sendPayment(
  args: PaymentArgs,
  callbacks: PaymentCallbacks = {}
): Promise<PaymentResult> {
  const { network, to, amount, token } = args;
  const { onSwitching, onAwaitingApproval, onAwaitingSignature, onBroadcasting } = callbacks;

  // Validate inputs
  if (!isValidNetwork(network)) {
    throw new PaymentError(`Unsupported network: ${network}`, 'UNSUPPORTED_NETWORK');
  }

  if (!to || !to.startsWith('0x') || to.length !== 42) {
    throw new PaymentError(`Invalid recipient address: ${to}`, 'INVALID_ADDRESS');
  }

  // Use Number() instead of parseFloat() for better validation
  const numAmount = Number(amount);
  if (!amount || !Number.isFinite(numAmount) || numAmount <= 0) {
    throw new PaymentError(`Invalid amount: ${amount}`, 'INVALID_AMOUNT');
  }

  const targetChainId = NETWORK_CONFIGS[network].chainId;
  const currentChainId = getChainId(wagmiAdapter.wagmiConfig);

  try {
    // Switch network if needed
    if (currentChainId !== targetChainId) {
      onSwitching?.();
      console.log(`🔄 Switching from chain ${currentChainId} to ${targetChainId}`);
      
      await switchChain(wagmiAdapter.wagmiConfig, { chainId: targetChainId });
      
      // Wait for switch to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Prepare transaction parameters
    const txParams = {
      chainId: targetChainId,
      toAddress: to,
      amount,
      tokenAddress: token?.address,
      decimals: token?.decimals,
      isBaseUnits: false // We're passing human-readable amounts
    };

    console.log('🚀 Sending payment:', {
      network,
      to,
      amount,
      isToken: !!token,
      tokenAddress: token?.address
    });

    // For ERC-20 tokens, we might need approval first
    if (token?.address && token.intent !== 'transferFrom') {
      onAwaitingApproval?.();
      // Note: sendEVMTransaction handles approvals internally if needed
    }

    onAwaitingSignature?.();
    
    // Send the transaction
    const hash = await sendEVMTransaction(txParams);
    
    onBroadcasting?.();
    console.log('✅ Payment sent:', hash);

    return { hash, success: true };

  } catch (error: any) {
    console.error('❌ Payment failed:', error);
    throw mapError(error);
  }
}

/**
 * Map various error types to user-friendly messages
 */
export function mapError(error: any): PaymentError {
  if (error instanceof PaymentError) {
    return error;
  }

  if (error instanceof EVMTransactionError) {
    switch (error.code) {
      case 'USER_REJECTED':
        return new PaymentError(
          'User rejected transaction',
          'USER_REJECTED',
          'Transaction was rejected in your wallet'
        );
      case 'INSUFFICIENT_FUNDS':
        return new PaymentError(
          'Insufficient funds',
          'INSUFFICIENT_FUNDS',
          'Insufficient funds for this transaction'
        );
      case 'WRONG_CHAIN':
        return new PaymentError(
          'Wrong network',
          'WRONG_CHAIN',
          'Please switch to the correct network in your wallet'
        );
      default:
        return new PaymentError(
          error.message,
          'TRANSACTION_FAILED',
          'Transaction failed. Please try again.'
        );
    }
  }

  // Handle common wallet errors
  if (error.code === 4001 || error.code === '4001') {
    return new PaymentError(
      'User rejected transaction',
      'USER_REJECTED',
      'Transaction was rejected in your wallet'
    );
  }

  if (error.code === 4902 || error.code === '4902') {
    return new PaymentError(
      'Network not added to wallet',
      'NETWORK_NOT_ADDED',
      'Please add this network to your wallet and try again'
    );
  }

  if (error.message?.includes('insufficient funds')) {
    return new PaymentError(
      'Insufficient funds',
      'INSUFFICIENT_FUNDS',
      'Insufficient funds for this transaction'
    );
  }

  if (error.message?.includes('User denied')) {
    return new PaymentError(
      'User rejected transaction',
      'USER_REJECTED',
      'Transaction was rejected in your wallet'
    );
  }

  // Generic fallback
  return new PaymentError(
    error.message || 'Unknown error',
    'UNKNOWN_ERROR',
    'Payment failed. Please try again.'
  );
}

export type { PaymentArgs, PaymentCallbacks, PaymentResult, SupportedNetwork, TokenArgs };